import React, { useMemo } from "react";
import { LabelType, MenuLink } from "../../../../types";
import { Button } from "../../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../../common/Grid";
import { Input, Label } from "../../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { useStores } from "../../../../hooks/useStores";
import { Select } from "../../../common/Select";
import i18n from "../../../../i18n";
import Feedback from "../../../UI/Forms/FormElements/Feedback";
import { observer } from "mobx-react";
import { languages } from "../../../../config/config";

type Props = {
  menuLink?: MenuLink;
  isSubmitting: boolean;
  onSubmit: (values: MenuLink) => void;
  onCancel: () => void;
};

const MenuLinkForm: React.FC<Props> = observer(
  ({ menuLink, isSubmitting, onSubmit, onCancel }) => {
    const { categoryStore, configStore } = useStores();

    const { t } = useTranslation();

    const createLabelsObject = (array: any, labels: LabelType) => {
      let labelsObject = array.reduce((acc: any, obj: any) => {
        return {
          ...acc,
          [obj.lang]: labels[obj.lang] ? labels[obj.lang] : "",
        };
      }, {});
      return labelsObject;
    };

    const createLabelsYupObject = (array: any) => {
      let labelsObject = array.reduce((acc: any, obj: any) => {
        return {
          ...acc,
          [obj.lang]: Yup.string().required(
            t("components.common.menuLinkForm.langRequired")
          ),
        };
      }, {});
      return labelsObject;
    };

    const formik = useFormik({
      initialValues: {
        id: menuLink?.id || undefined,
        name: menuLink?.name || "",
        onClickUrl: menuLink?.onClickUrl || "",
        category:
          !!menuLink && !!menuLink.category ? menuLink.category : undefined,
        multimediaCategory:
          !!menuLink && !!menuLink.multimediaCategory
            ? menuLink.multimediaCategory
            : undefined,
        labels: createLabelsObject(
          languages,
          menuLink?.labels ? menuLink.labels : {}
        ),
        icon: menuLink?.icon,
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape(
        {
          icon: Yup.object().required(
            t("components.common.menuLinkForm.iconRequired")
          ),
          name: Yup.string().required(
            t("components.common.menuLinkForm.nameRequired")
          ),
          labels: Yup.object().shape(createLabelsYupObject(languages)),
          onClickUrl: Yup.string().when(["category", "multimediaCategory"], {
            is: (category: any, multimediaCategory: any) =>
              !category && !multimediaCategory,
            then: Yup.string().required(
              t("components.common.menuLinkForm.onClickUrlCategoryRequired")
            ),
            otherwise: Yup.string(),
          }),
          category: Yup.object().when(["onClickUrl", "multimediaCategory"], {
            is: (onClickUrl: string, multimediaCategory: any) =>
              !onClickUrl && !multimediaCategory,
            then: Yup.object().required(
              t("components.common.menuLinkForm.onClickUrlCategoryRequired")
            ),
            otherwise: Yup.object(),
          }),
          multimediaCategory: Yup.object().when(["onClickUrl", "category"], {
            is: (onClickUrl: string, category: any) => !onClickUrl && !category,
            then: Yup.object().required(
              t("components.common.menuLinkForm.onClickUrlCategoryRequired")
            ),
            otherwise: Yup.object(),
          }),
        },
        [
          ["onClickUrl", "category"],
          ["multimediaCategory", "category"],
          ["onClickUrl", "multimediaCategory"],
        ]
      ),
    });

    const programCategoryOptions = useMemo(() => {
      return categoryStore.programCategories.map((programCategory) => ({
        label: programCategory.labels[i18n.language],
        value: programCategory.id,
      }));
    }, [categoryStore.programCategories]);

    const multimediaCategoryOptions = useMemo(() => {
      return categoryStore.multimediaCategories.map((multimediaCategory) => ({
        label: multimediaCategory.labels[i18n.language],
        value: multimediaCategory.id,
      }));
    }, [categoryStore.multimediaCategories]);

    const iconOptions = useMemo(() => {
      return configStore.iconsList.map((icon) => ({
        icon: icon.iconUrl,
        label: icon.iconUrl,
        value: icon.id,
      }));
    }, [configStore.iconsList]);

    return (
      <form onSubmit={formik.handleSubmit}>
        <Container pb={"20px"}>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="icon">
                  {t("components.common.menuLinkForm.icon")}
                </Label>
                <Select
                  id="icon"
                  name="icon"
                  icon={true}
                  options={iconOptions}
                  isMulti={false}
                  value={
                    formik.values.icon
                      ? {
                          label: formik.values.icon.icon.iconUrl,
                          value: formik.values.icon.id,
                        }
                      : {}
                  }
                  onChange={(option: any) => {
                    formik.setFieldValue("icon", {
                      icon: { id: option.value, iconUrl: option.icon },
                    });
                  }}
                />
                {formik.errors.icon && (
                  <Feedback state="error" showState={true} showErrorOnly={true}>
                    {formik.getFieldMeta("icon").error}
                  </Feedback>
                )}
              </FormControl>
            </Col>
          </Row>
          <Row mt="10px">
            <Col noGutter>
              <FormControl fullWidth>
                <Label htmlFor="name">
                  {t("components.common.menuLinkForm.name")}
                </Label>
                <Input
                  id="name"
                  name="name"
                  placeholder={t(
                    "components.common.menuLinkForm.namePlaceholder"
                  )}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  state={formik.errors.name ? "error" : "success"}
                  showState={true}
                  feedbackText={formik.getFieldMeta("name").error}
                />
              </FormControl>
            </Col>
          </Row>
          {languages.map((language: any) => {
            const lang = language.lang;
            return (
              <Row paddingTop="10px">
                <Col noGutter>
                  <FormControl fullWidth>
                    <Label htmlFor="as">{language.description}</Label>
                    <Input
                      id={`labels.${lang}`}
                      name={`labels.${lang}`}
                      placeholder={t(
                        "components.common.menuLinkForm.namePlaceholder"
                      )}
                      value={formik.values.labels[lang] as unknown as string}
                      onChange={formik.handleChange}
                      state={formik.errors.labels?.[lang] ? "error" : "success"}
                      showState={true}
                      feedbackText={formik.getFieldMeta(`labels.${lang}`).error}
                    />
                  </FormControl>
                </Col>
              </Row>
            );
          })}
          {!formik.values.category && !formik.values.multimediaCategory && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="onClickUrl">
                    {t("components.common.menuLinkForm.onClickUrl")}
                  </Label>
                  <Input
                    id="onClickUrl"
                    name="onClickUrl"
                    placeholder={t(
                      "components.common.menuLinkForm.onClickUrlPlaceholder"
                    )}
                    value={formik.values.onClickUrl}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.setFieldValue("category", undefined);
                    }}
                    state={formik.errors.onClickUrl ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("onClickUrl").error}
                  />
                </FormControl>
              </Col>
            </Row>
          )}
          {!formik.values.onClickUrl && !formik.values.multimediaCategory && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="category">
                    {t("components.common.menuLinkForm.category")}
                  </Label>
                  <Select
                    id="category"
                    name="category"
                    options={programCategoryOptions}
                    isMulti={false}
                    value={
                      formik.values.category &&
                      typeof formik.values.category !== "number"
                        ? {
                            label: formik.values.category.labels[i18n.language],
                            value: formik.values.category!.id,
                          }
                        : {}
                    }
                    onChange={(option: any) => {
                      formik.setFieldValue(
                        "category",
                        categoryStore.programCategories.find(
                          (cat) => cat.id === option.value
                        )
                      );
                    }}
                  />
                  {formik.errors.category && (
                    <Feedback
                      state="error"
                      showState={true}
                      showErrorOnly={true}
                    >
                      {formik.getFieldMeta("category").error}
                    </Feedback>
                  )}
                </FormControl>
              </Col>
            </Row>
          )}
          {!formik.values.onClickUrl && !formik.values.category && (
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="multimediaCategory">
                    {t("components.common.menuLinkForm.multimediaCategory")}
                  </Label>
                  <Select
                    id="multimediaCategory"
                    name="multimediaCategory"
                    options={multimediaCategoryOptions}
                    isMulti={false}
                    value={
                      formik.values.multimediaCategory &&
                      typeof formik.values.multimediaCategory !== "number"
                        ? {
                            label:
                              formik.values.multimediaCategory.labels[
                                i18n.language
                              ],
                            value: formik.values.multimediaCategory!.id,
                          }
                        : {}
                    }
                    onChange={(option: any) => {
                      formik.setFieldValue(
                        "multimediaCategory",
                        categoryStore.multimediaCategories.find(
                          (cat) => cat.id === option.value
                        )
                      );
                    }}
                  />
                  {formik.errors.multimediaCategory && (
                    <Feedback
                      state="error"
                      showState={true}
                      showErrorOnly={true}
                    >
                      {formik.getFieldMeta("multimediaCategory").error}
                    </Feedback>
                  )}
                </FormControl>
              </Col>
            </Row>
          )}
        </Container>

        <Row>
          <Col textAlign="end">
            <Button
              type={"reset"}
              onClick={onCancel}
              marginRight="10px"
              color="light"
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button type={"submit"}>{t("common.save")}</Button>
          </Col>
        </Row>
      </form>
    );
  }
);

export default MenuLinkForm;
